// src/utils/notesMediaUpload.js
// PHASE 1.7 VERIFIER: r1-17a-web-3c8f4e2a
//
// Helpers for the Phase 1.7 Slice A photo-upload pipeline used by
// NoteComposer.js. Pure async functions — no React, no UI state. The
// composer owns the state machine and calls these for the side-effects.
//
// Pipeline per photo:
//   1. validateFile(file)               — throws on bad input
//   2. compressPhoto(file)              — max edge 2048px, JPEG q0.85
//   3. createNotesMediaDoc(...)         — Firestore notesMedia/{mediaId}
//   4. uploadPhotoToStorage(...)        — Firebase Storage, with progress
//   5. removeStagedPhoto(...)           — cleanup if user removes the tile
//
// ── Defense-in-depth notes ────────────────────────────────────────────────
//   1. Client-side caps are belt-only — server-side rules also enforce
//      org-membership and the 10MB Storage size limit. A malicious client
//      that bypassed these checks would be rejected at the rule layer.
//   2. mediaId is generated client-side via uuidv4 — collision risk is
//      effectively zero (2^122 namespace) and matches the convention used
//      elsewhere in the codebase (uuid is already a dep).
//   3. uploadStatus is set to 'pending' on doc create; the Storage trigger
//      flips it to 'uploaded'. publicAttachments is always [] from the
//      client; the onNoteCreated CF computes the real value.
//   4. removeStagedPhoto is best-effort — Storage delete + thumb delete +
//      Firestore doc delete each have their own try/catch. A partial
//      failure leaves an orphan that the future cleanup CF will collect.

import imageCompression from 'browser-image-compression';
import {
  doc, setDoc, deleteDoc, serverTimestamp,
} from 'firebase/firestore';
import {
  ref, uploadBytesResumable, deleteObject,
} from 'firebase/storage';
import { v4 as uuidv4 } from 'uuid';
import { auth, db, storage } from '../firebase/config';

// --- Configuration -------------------------------------------------------

export const MAX_PHOTOS_PER_NOTE = 10;
export const MAX_RAW_BYTES = 25 * 1024 * 1024;       // 25MB pre-compression
export const MAX_EDGE_PX = 2048;
export const JPEG_QUALITY = 0.85;
export const ALLOWED_MIME_PREFIX = 'image/';

// --- Validation ----------------------------------------------------------

/**
 * Throws an Error with a user-facing message if the file is invalid.
 * Callers should catch and surface inline.
 */
export const validateFile = (file) => {
  if (!file) {
    throw new Error('No file provided.');
  }
  if (!file.type || !file.type.startsWith(ALLOWED_MIME_PREFIX)) {
    throw new Error('Only image files are supported.');
  }
  if (file.size > MAX_RAW_BYTES) {
    throw new Error('Photo too large — maximum 25MB per file.');
  }
};

// --- Compression ---------------------------------------------------------

/**
 * Compress a File to a JPEG Blob with max edge MAX_EDGE_PX at JPEG_QUALITY.
 * Returns a File (browser-image-compression returns File or Blob; we coerce
 * to File so we always have a name).
 *
 * EXIF orientation is preserved by the library (it draws to canvas with
 * orientation baked in) — matches the .rotate() call in the server-side
 * sharp pipeline so the thumbnail and the original have consistent
 * orientation.
 */
export const compressPhoto = async (file) => {
  const compressed = await imageCompression(file, {
    maxSizeMB: 5,             // soft target — usually lands well under
    maxWidthOrHeight: MAX_EDGE_PX,
    useWebWorker: true,
    fileType: 'image/jpeg',
    initialQuality: JPEG_QUALITY,
  });
  // Library returns Blob in some paths; ensure we have a File-like with name.
  if (compressed instanceof File) return compressed;
  return new File([compressed], `${uuidv4()}.jpg`, { type: 'image/jpeg' });
};

// --- mediaId + path helpers ---------------------------------------------

export const newMediaId = () => uuidv4();

export const buildStoragePath = (orgId, noteId, mediaId) =>
  `organizations/${orgId}/notes-media/${noteId}/${mediaId}.jpg`;

export const buildThumbPath = (orgId, noteId, mediaId) =>
  `organizations/${orgId}/notes-media/${noteId}/${mediaId}_thumb.jpg`;

// --- Firestore notesMedia doc -------------------------------------------

/**
 * Create the parallel notesMedia/{mediaId} doc that the onNoteCreated CF
 * will propagate publicAttachments + createdBy onto when the note lands.
 *
 * Required fields per firestore.rules isValidMediaCreate():
 *   createdBy === request.auth.uid
 *   type in ['photo', 'video', 'voice']
 *   noteId is string
 *   uploadStatus in ['pending', 'uploaded', 'failed']
 *   publicAttachments == []
 */
export const createNotesMediaDoc = async ({
  orgId, mediaId, noteId, currentUserId,
}) => {
  const mediaRef = doc(db, `organizations/${orgId}/notesMedia/${mediaId}`);
  await setDoc(mediaRef, {
    createdBy: currentUserId,
    type: 'photo',
    noteId,
    uploadStatus: 'pending',
    publicAttachments: [],
    createdAt: serverTimestamp(),
  });
};

// --- Storage upload ------------------------------------------------------

/**
 * Upload a compressed File to Firebase Storage with progress callbacks.
 * Returns an object exposing:
 *   - promise: resolves to { storagePath } on completion; rejects on error
 *   - cancel():  aborts the in-flight upload (component-unmount safety)
 *
 * Caller wires onProgress (0..1 fraction) into the UI tile.
 */
export const uploadPhotoToStorage = ({
  file, orgId, noteId, mediaId, onProgress,
}) => {
  const storagePath = buildStoragePath(orgId, noteId, mediaId);
  const storageRef = ref(storage, storagePath);
  // s9storage-5d2a8e17 — stamp who uploaded it. storage.rules lets ONLY that person delete the
  // photo (removeStagedPhoto, when they X a staged tile); before S9a every delete was refused.
  const uploaderUid = auth.currentUser?.uid;
  const task = uploadBytesResumable(storageRef, file, {
    contentType: 'image/jpeg',
    cacheControl: 'public, max-age=31536000', // immutable per mediaId
    ...(uploaderUid ? { customMetadata: { uploadedBy: uploaderUid } } : {}),
  });

  const promise = new Promise((resolve, reject) => {
    task.on(
      'state_changed',
      (snap) => {
        if (typeof onProgress === 'function' && snap.totalBytes > 0) {
          onProgress(snap.bytesTransferred / snap.totalBytes);
        }
      },
      (err) => reject(err),
      () => resolve({ storagePath })
    );
  });

  return {
    promise,
    cancel: () => {
      try {
        task.cancel();
      } catch (_) {
        // already finished or cancelled — ignore
      }
    },
  };
};

// --- Cleanup -------------------------------------------------------------

/**
 * Best-effort cleanup for a staged photo the user removed from the tray.
 * Each step has its own try/catch — a missing file or doc is not an error
 * (we may be cleaning up something that never finished uploading).
 *
 * Called when:
 *   - User clicks the X on a staged tile that's already uploaded
 *   - User clicks the X on a failed tile (notesMedia doc may exist)
 *   - Composer unmounts mid-upload (best-effort orphan reduction)
 */
export const removeStagedPhoto = async ({
  orgId, noteId, mediaId, hadUploaded,
}) => {
  // Delete the original from Storage if it was uploaded.
  if (hadUploaded) {
    try {
      await deleteObject(ref(storage, buildStoragePath(orgId, noteId, mediaId)));
    } catch (_) { /* not-found is fine */ }

    // Delete the thumbnail if the Storage trigger already generated it.
    try {
      await deleteObject(ref(storage, buildThumbPath(orgId, noteId, mediaId)));
    } catch (_) { /* not-found is fine */ }
  }

  // Delete the notesMedia Firestore doc. May not exist if the user removed
  // the tile before compression finished — that's fine.
  try {
    await deleteDoc(doc(db, `organizations/${orgId}/notesMedia/${mediaId}`));
  } catch (_) { /* not-found is fine */ }
};